import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 12;

export function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("polling");
  const [purpose, setPurpose] = useState(null);
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let polls = 0;
    const poll = async () => {
      if (polls >= MAX_POLLS) { setStatus("timeout"); return; }
      polls++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPurpose(data.purpose); setJobId(data.job_id);
        if (data.payment_status === "paid") { setStatus("success"); await checkAuth(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch (error) { console.debug("Payment status poll retrying:", error?.message); }
      setTimeout(poll, 2000);
    };
    poll();
  }, [checkAuth]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center" data-testid="payment-success-card">
          {status === "polling" && <><Loader2 className="h-10 w-10 animate-spin text-amber-500 mx-auto" /><h1 className="font-display text-xl font-bold text-navy-800 mt-4">Confirming your payment…</h1><p className="text-slate-500 text-sm mt-2">This only takes a moment.</p></>}
          {status === "success" && (
            <>
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><CheckCircle2 className="h-9 w-9 text-emerald-600" /></div>
              <h1 className="font-display text-2xl font-extrabold text-navy-800 mt-4">Payment successful!</h1>
              <p className="text-slate-500 mt-2">{purpose === "job_post" ? "Your job listing is now live for 60 days." : "Your 30-day membership is active. Apply to unlimited jobs!"}</p>
              <Button data-testid="payment-continue-btn" onClick={() => navigate(purpose === "job_post" ? "/employer" : jobId ? `/jobs/${jobId}` : "/applicant")} className="w-full mt-6 bg-navy-800 hover:bg-navy-900 text-white">Continue</Button>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto"><XCircle className="h-9 w-9 text-red-600" /></div>
              <h1 className="font-display text-xl font-bold text-navy-800 mt-4">{status === "timeout" ? "Still processing" : "Payment issue"}</h1>
              <p className="text-slate-500 text-sm mt-2">{status === "timeout" ? "Your payment is taking longer than expected. Check your dashboard shortly." : "We couldn't confirm this payment."}</p>
              <Button onClick={() => navigate("/")} variant="outline" className="w-full mt-6">Back home</Button>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center">
          <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto"><XCircle className="h-9 w-9 text-slate-400" /></div>
          <h1 className="font-display text-xl font-bold text-navy-800 mt-4">Payment cancelled</h1>
          <p className="text-slate-500 text-sm mt-2">No charge was made. You can try again anytime.</p>
          <Button onClick={() => navigate(-1)} className="w-full mt-6 bg-navy-800 hover:bg-navy-900 text-white">Go back</Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
